"use client";

import React, { useState, useEffect } from 'react';
import { useGame } from '@/app/providers/GameContextProvider';
import { GameStep } from '@/lib/types/common/GameStep';
import { GameMode, GameHazard } from '@/lib/types/common/GameMode';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBug, FaChevronDown, FaChevronUp, FaUsers, FaPlay, FaStepForward, FaCrown, FaUserPlus } from 'react-icons/fa';

const isDev = process.env.NODE_ENV === 'development';

interface DevPanelProps {
    isEnabled?: boolean;
}

const DevPanel: React.FC<DevPanelProps> = ({ isEnabled = isDev }) => {
    const { room, gameState } = useGame();
    const [isOpen, setIsOpen] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [simulateMyGuess, setSimulateMyGuess] = useState(true);
    const [autoPerfectMyGuess, setAutoPerfectMyGuess] = useState(false);
    const [autoPerfectOthers, setAutoPerfectOthers] = useState(false);
    const [isHazardSectionOpen, setIsHazardSectionOpen] = useState(false);
    const [isTeamSectionOpen, setIsTeamSectionOpen] = useState(false);

    // Keyboard shortcut to toggle dev panel (Ctrl+Shift+D)
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.ctrlKey && e.shiftKey && e.code === 'KeyD') {
                e.preventDefault();
                setIsOpen(prev => !prev);
            }
        };

        if (isEnabled && isDev) {
            document.addEventListener('keydown', handleKeyPress);
            return () => document.removeEventListener('keydown', handleKeyPress);
        }
    }, [isEnabled, isDev, isOpen]);

    // Don't render if not enabled or in production
    if (!isEnabled || !isDev) {
        return null;
    }

    const gameSteps = [
        { value: GameStep.LOBBY, label: 'LOBBY', color: 'bg-blue-500' },
        { value: GameStep.GUESSING, label: 'GUESSING', color: 'bg-yellow-500' },
        { value: GameStep.RESULT, label: 'RESULT', color: 'bg-orange-500' },
        { value: GameStep.RECAP, label: 'RECAP', color: 'bg-green-500' },
    ];

    const gameModes = [
        { value: GameMode.CLASSIC, label: 'Classic' },
        { value: GameMode.TIMED, label: 'Timed' },
        { value: GameMode.FIGHT, label: 'Fight' },
        { value: GameMode.ENDLESS, label: 'Endless' },
        { value: GameMode.FLASH, label: 'Flash' },
        { value: GameMode.HAZARD, label: 'Hazard' },
        { value: GameMode.COOP, label: 'Coop' },
        { value: GameMode.DAILY, label: 'Daily' },
        { value: GameMode.CUSTOM, label: 'Custom' },
    ];

    const handleChangeGameStep = (step: GameStep) => {
        if (!room) return;
        
        room.send("devChangeGameStep", { gameStep: step });
    };

    const handleChangeGameMode = (mode: GameMode) => {
        if (!room) return;
        
        room.send("gameMode", { gameMode: mode });
    };

    const handleAddFakePlayers = async (count: number) => {
        if (!room || isAdding) return;
        
        setIsAdding(true);
        try {
            room.send("devAddFakePlayers", { count });
        } catch (error) {
            console.error("Failed to add fake players:", error);
        } finally {
            setIsAdding(false);
        }
    };

    const handleRemoveFakePlayers = () => {
        if (!room) return;
        
        room.send("devRemoveFakePlayers");
    };

    const handleAddBotToTeam = (teamId: string) => {
        if (!room) return;
        
        room.send("devAddBotToTeam", { teamId });
    };

    const handleDistributeBotsToTeams = (botsPerTeam: number) => {
        if (!room) return;
        
        room.send("devDistributeBotsToTeams", { botsPerTeam });
    };

    const handleSimulateGuesses = () => {
        if (!room) return;
        
        room.send("devSimulateGuesses", { 
            includeMyGuess: simulateMyGuess,
            perfectMyGuess: autoPerfectMyGuess,
            perfectOthers: autoPerfectOthers
        });
    };

    const handleNextRound = () => {
        if (!room) return;
        
        // First simulate guesses if we're in guessing phase
        if (gameState?.gameStep === GameStep.GUESSING) {
            room.send("devSimulateGuesses", { 
                includeMyGuess: simulateMyGuess,
                perfectMyGuess: autoPerfectMyGuess,
                perfectOthers: autoPerfectOthers
            });
            // Wait a bit then proceed to next round
            setTimeout(() => {
                room.send("nextRound");
            }, 100);
        } else {
            room.send("nextRound");
        }
    };

    const handleStartGame = () => {
        if (!room) return;
        
        room.send("startGame");
    };

    const handleQuickTest = async () => {
        if (!room || isAdding) return;
        
        setIsAdding(true);
        try {
            // Add some fake players if we don't have many
            if (totalPlayerCount < 3) {
                room.send("devAddFakePlayers", { count: 3 });
            }
            
            // Start the game after a short delay
            setTimeout(() => {
                room.send("startGame");
            }, 500);
        } catch (error) {
            console.error("Failed to setup quick test:", error);
        } finally {
            setIsAdding(false);
        }
    };

    const handleAssignRandomLeader = () => {
        if (!room) return;
        
        room.send("devAssignRandomLeader");
    };

    const handleTakeBackLeader = () => {
        if (!room) return;
        
        room.send("devTakeBackLeader");
    };

    const handleSetHazard = (hazard: GameHazard | "") => {
        if (!room) return;
        
        if (hazard === "") {
            // Clear all hazards
            room.send("devSetHazards", { hazards: [] });
        } else {
            // Toggle the hazard
            const currentHazards = gameState?.currentHazards ? Array.from(gameState.currentHazards) : [];
            const index = currentHazards.indexOf(hazard);
            
            if (index >= 0) {
                // Remove if already present
                currentHazards.splice(index, 1);
            } else {
                // Add if not present
                currentHazards.push(hazard);
            }
            
            room.send("devSetHazards", { hazards: currentHazards });
        }
    };

    // Get all available hazards dynamically from the enum
    const availableHazards = Object.values(GameHazard);

    const currentStep = gameSteps.find(step => step.value === gameState?.gameStep);
    const currentMode = gameModes.find(mode => mode.value === gameState?.gameMode);
    
    const realPlayerCount = gameState ? Array.from(gameState.players?.entries() || []).filter(([, player]) => !player.name.startsWith('Bot ')).length : 0;
    const totalPlayerCount = gameState ? (gameState.players?.size || 0) : 0;

    return (
        <div className="fixed top-16 right-4 z-[9999]">
            <motion.div 
                className="bg-black/90 backdrop-blur-sm rounded-lg shadow-2xl border border-red-500/50 overflow-hidden"
                initial={false}
                animate={{ 
                    width: isOpen ? 350 : 70,
                    height: isOpen ? 'auto' : 48
                }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
            >
                {/* Header */}
                <div 
                    className="flex items-center justify-between p-3 cursor-pointer select-none"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <div className="flex items-center gap-2">
                        <FaBug className="text-red-500 text-xl" />
                        {isOpen && (
                            <div className="flex items-center gap-2">
                                <span className="text-white font-bold text-sm">DEV TOOLS</span>
                                <span className="text-white/50 text-xs">(Ctrl+Shift+D)</span>
                            </div>
                        )}
                    </div>
                    {isOpen ? (
                        <FaChevronUp className="text-white/70 ml-2" />
                    ) : (
                        <FaChevronDown className="text-white/70 ml-2" />
                    )}
                </div>

                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="px-3 pb-3 space-y-4"
                        >
                            {/* Game State Info */}
                            <div className="space-y-2">
                                <div className="text-white/70 text-xs font-semibold uppercase tracking-wider">Current State</div>
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <div className={`w-2 h-2 rounded-full ${currentStep?.color || 'bg-gray-500'}`}></div>
                                        <span className="text-white text-sm">{currentStep?.label || 'UNKNOWN'}</span>
                                    </div>
                                    <div className="text-white/70 text-xs">
                                        Mode: {currentMode?.label || 'Unknown'}
                                    </div>
                                    <div className="text-white/70 text-xs">
                                        Round: {gameState?.currentRound || 0}/{gameState?.maxRounds || 0}
                                    </div>
                                    <div className="text-white/70 text-xs">
                                        Players: {realPlayerCount} real + {totalPlayerCount - realPlayerCount} bots = {totalPlayerCount} total
                                    </div>
                                </div>
                            </div>

                            {/* Game Step Control */}
                            <div className="space-y-2">
                                <div className="text-white/70 text-xs font-semibold uppercase tracking-wider">Game Steps</div>
                                <div className="grid grid-cols-2 gap-1">
                                    {gameSteps.map((step) => (
                                        <button
                                            key={step.value}
                                            onClick={() => handleChangeGameStep(step.value)}
                                            className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                                currentStep?.value === step.value
                                                    ? 'bg-white text-black'
                                                    : 'bg-white/10 text-white hover:bg-white/20'
                                            }`}
                                        >
                                            {step.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Game Mode Control */}
                            <div className="space-y-2">
                                <div className="text-white/70 text-xs font-semibold uppercase tracking-wider">Game Mode</div>
                                <select
                                    value={gameState?.gameMode || GameMode.CLASSIC}
                                    onChange={(e) => handleChangeGameMode(e.target.value as GameMode)}
                                    className="w-full bg-white/10 text-white text-sm rounded px-2 py-1 border border-white/20"
                                >
                                    {gameModes.map((mode) => (
                                        <option key={mode.value} value={mode.value} className="bg-black">
                                            {mode.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Hazard Testing (only show in Hazard mode or Custom mode with hazards) */}
                            {(gameState?.gameMode === GameMode.HAZARD || 
                              (gameState?.gameMode === GameMode.CUSTOM && gameState?.gameSettings?.hasHazards)) && (
                                <div className="space-y-2">
                                    <div 
                                        className="text-white/70 text-xs font-semibold uppercase tracking-wider cursor-pointer hover:text-white transition-colors flex items-center justify-between"
                                        onClick={() => setIsHazardSectionOpen(!isHazardSectionOpen)}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span>Hazard Testing</span>
                                            {gameState.currentHazards && gameState.currentHazards.length > 0 && (
                                                <span className="text-yellow-400 normal-case font-normal">
                                                    ({gameState.currentHazards.join(', ')})
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <FaChevronUp className="text-xs" />
                                        </div>
                                    </div>
                                    
                                    <AnimatePresence>
                                        {isHazardSectionOpen && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                exit={{ opacity: 0, height: 0 }}
                                                transition={{ duration: 0.2 }}
                                                className="grid grid-cols-2 gap-1"
                                            >
                                                {availableHazards.map((hazard) => (
                                                    <button
                                                        key={hazard}
                                                        onClick={() => handleSetHazard(hazard)}
                                                        className={`px-2 py-1 rounded text-xs font-medium transition-colors capitalize bg-white/10 text-white hover:bg-white/20 ${
                                                            gameState.currentHazards && gameState.currentHazards.includes(hazard)
                                                                ? '!bg-white !text-black'
                                                                : ''
                                                        }`}
                                                    >
                                                        {hazard}
                                                    </button>
                                                ))}
                                                <button
                                                    onClick={() => handleSetHazard("")}
                                                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                                        !gameState.currentHazards || gameState.currentHazards.length === 0
                                                            ? 'bg-white text-black'
                                                            : 'bg-white/10 text-white hover:bg-white/20'
                                                    }`}
                                                >
                                                    None
                                                </button>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            )}

                            {/* Fake Players Control */}
                            <div className="space-y-2">
                                <div className="text-white/70 text-xs font-semibold uppercase tracking-wider">Fake Players</div>
                                <div className="grid grid-cols-3 gap-1">
                                    {[1, 5, 10].map(count => (
                                        <button
                                            key={count}
                                            onClick={() => handleAddFakePlayers(count)}
                                            disabled={isAdding || totalPlayerCount >= 20}
                                            className="bg-green-600/80 hover:bg-green-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-xs px-2 py-1 rounded font-medium transition-colors"
                                        >
                                            +{count}
                                        </button>
                                    ))}
                                </div>
                                <button
                                    onClick={handleRemoveFakePlayers}
                                    disabled={totalPlayerCount <= realPlayerCount}
                                    className="w-full bg-red-600/80 hover:bg-red-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-xs px-2 py-1 rounded font-medium transition-colors"
                                >
                                    Remove All Bots
                                </button>
                            </div>

                            {/* Team Bot Management (only show in Fight/Coop mode or Custom mode with teams) */}
                            {(gameState?.gameMode === GameMode.FIGHT ||
                              gameState?.gameMode === GameMode.COOP ||
                              (gameState?.gameMode === GameMode.CUSTOM && 
                               (gameState?.gameSettings?.baseMode === GameMode.FIGHT || gameState?.gameSettings?.baseMode === GameMode.COOP))) &&
                             gameState?.teams && gameState.teams.length > 0 && (
                                <div className="space-y-2">
                                    <div 
                                        className="text-white/70 text-xs font-semibold uppercase tracking-wider cursor-pointer hover:text-white transition-colors flex items-center justify-between"
                                        onClick={() => setIsTeamSectionOpen(!isTeamSectionOpen)}
                                    >
                                        <div className="flex items-center gap-2">
                                            <FaUserPlus className="text-xs" />
                                            <span>Team Bots</span>
                                            <span className="text-cyan-400 normal-case font-normal">
                                                ({gameState.teams.length} teams)
                                            </span>
                                        </div>
                                        {isTeamSectionOpen ? <FaChevronUp className="text-xs" /> : <FaChevronDown className="text-xs" />}
                                    </div>
                                    
                                    <AnimatePresence>
                                        {isTeamSectionOpen && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                exit={{ opacity: 0, height: 0 }}
                                                transition={{ duration: 0.2 }}
                                                className="space-y-2"
                                            >
                                                {/* Quick distribute buttons */}
                                                <div className="flex gap-1">
                                                    <button
                                                        onClick={() => handleDistributeBotsToTeams(1)}
                                                        disabled={totalPlayerCount >= 20}
                                                        className="flex-1 bg-cyan-600/80 hover:bg-cyan-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-xs px-2 py-1 rounded font-medium transition-colors"
                                                    >
                                                        +1 each
                                                    </button>
                                                    <button
                                                        onClick={() => handleDistributeBotsToTeams(2)}
                                                        disabled={totalPlayerCount >= 20}
                                                        className="flex-1 bg-cyan-600/80 hover:bg-cyan-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-xs px-2 py-1 rounded font-medium transition-colors"
                                                    >
                                                        +2 each
                                                    </button>
                                                    <button
                                                        onClick={() => handleDistributeBotsToTeams(3)}
                                                        disabled={totalPlayerCount >= 20}
                                                        className="flex-1 bg-cyan-600/80 hover:bg-cyan-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-xs px-2 py-1 rounded font-medium transition-colors"
                                                    >
                                                        +3 each
                                                    </button>
                                                </div>

                                                {/* Individual team controls */}
                                                <div className="space-y-1">
                                                    {Array.from(gameState.teams).map((team) => {
                                                        const teamColor = {
                                                            red: 'border-red-500 bg-red-900/30',
                                                            blue: 'border-blue-500 bg-blue-900/30',
                                                            green: 'border-green-500 bg-green-900/30',
                                                            yellow: 'border-yellow-500 bg-yellow-900/30',
                                                            purple: 'border-purple-500 bg-purple-900/30',
                                                            pink: 'border-pink-500 bg-pink-900/30',
                                                            orange: 'border-orange-500 bg-orange-900/30',
                                                            cyan: 'border-cyan-500 bg-cyan-900/30',
                                                            indigo: 'border-indigo-500 bg-indigo-900/30',
                                                            teal: 'border-teal-500 bg-teal-900/30'
                                                        }[team.color] || 'border-gray-500 bg-gray-900/30';
                                                        
                                                        return (
                                                            <div 
                                                                key={team.id} 
                                                                className={`flex items-center justify-between p-2 rounded border ${teamColor}`}
                                                            >
                                                                <div className="flex-1">
                                                                    <div className="text-white text-xs font-medium truncate">
                                                                        {team.name}
                                                                    </div>
                                                                    <div className="text-white/50 text-[10px]">
                                                                        {team.memberSessionIds?.length || 0} members
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    onClick={() => handleAddBotToTeam(team.id)}
                                                                    disabled={totalPlayerCount >= 20}
                                                                    className="bg-white/20 hover:bg-white/30 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-xs px-2 py-1 rounded font-medium transition-colors flex items-center gap-1"
                                                                >
                                                                    <FaUserPlus className="text-[10px]" />
                                                                    +Bot
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            )}

                            {/* Game Actions */}
                            <div className="space-y-2">
                                <div className="text-white/70 text-xs font-semibold uppercase tracking-wider">Actions</div>
                                <div className="space-y-1">
                                    <button
                                        onClick={handleQuickTest}
                                        disabled={isAdding}
                                        className="w-full bg-pink-600/80 hover:bg-pink-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-xs px-2 py-1 rounded font-medium transition-colors flex items-center justify-center gap-2"
                                    >
                                        <FaBug className="text-xs" />
                                        Quick Test
                                    </button>

                                    <button
                                        onClick={handleStartGame}
                                        disabled={gameState?.gameStep !== GameStep.LOBBY}
                                        className="w-full bg-blue-600/80 hover:bg-blue-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-xs px-2 py-1 rounded font-medium transition-colors flex items-center justify-center gap-2"
                                    >
                                        <FaPlay className="text-xs" />
                                        Start Game
                                    </button>
                                    
                                    <button
                                        onClick={handleSimulateGuesses}
                                        disabled={gameState?.gameStep !== GameStep.GUESSING}
                                        className="w-full bg-yellow-600/80 hover:bg-yellow-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-xs px-2 py-1 rounded font-medium transition-colors flex items-center justify-center gap-2"
                                    >
                                        <FaUsers className="text-xs" />
                                        Simulate Guesses
                                    </button>
                                    
                                    <button
                                        onClick={handleNextRound}
                                        disabled={gameState?.gameStep !== GameStep.RESULT}
                                        className="w-full bg-orange-600/80 hover:bg-orange-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-xs px-2 py-1 rounded font-medium transition-colors flex items-center justify-center gap-2"
                                    >
                                        <FaStepForward className="text-xs" />
                                        Next Round
                                    </button>
                                    
                                    <button
                                        onClick={handleAssignRandomLeader}
                                        className="w-full bg-purple-600/80 hover:bg-purple-600 text-white text-xs px-2 py-1 rounded font-medium transition-colors flex items-center justify-center gap-2"
                                    >
                                        <FaCrown className="text-xs" />
                                        Assign Random Leader
                                    </button>
                                    
                                    <button
                                        onClick={handleTakeBackLeader}
                                        className="w-full bg-gray-600/80 hover:bg-gray-600 text-white text-xs px-2 py-1 rounded font-medium transition-colors flex items-center justify-center gap-2"
                                    >
                                        Take Back Leader
                                    </button>
                                </div>
                            </div>

                            {/* Dev Config Button */}
                            <div className="space-y-2">
                                <div className="text-white/70 text-xs font-semibold uppercase tracking-wider">Options</div>
                                <label className="flex items-center justify-between text-white text-sm">
                                    <span>Simulate My Guess</span>
                                    <input
                                        type="checkbox"
                                        checked={simulateMyGuess}
                                        onChange={(e) => setSimulateMyGuess(e.target.checked)}
                                        className="ml-2"
                                    />
                                </label>
                                <label className="flex items-center justify-between text-white text-sm">
                                    <span>Auto Perfect My Guess</span>
                                    <input
                                        type="checkbox"
                                        checked={autoPerfectMyGuess}
                                        onChange={(e) => setAutoPerfectMyGuess(e.target.checked)}
                                        className="ml-2"
                                    />
                                </label>
                                <label className="flex items-center justify-between text-white text-sm">
                                    <span>Auto Perfect Others</span>
                                    <input
                                        type="checkbox"
                                        checked={autoPerfectOthers}
                                        onChange={(e) => setAutoPerfectOthers(e.target.checked)}
                                        className="ml-2"
                                    />
                                </label>
                            </div>

                            {/* Connection Info */}
                            <div className="border-t border-white/20 pt-2">
                                <div className="text-white/50 text-xs">
                                    Room ID: {room?.roomId || 'None'}
                                </div>
                                <div className="text-white/50 text-xs">
                                    Session: {room?.sessionId?.substring(0, 8) || 'None'}...
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
    );
};

export default DevPanel;